/**
 * Auth API functions
 *
 * These functions communicate with the backend auth endpoints.
 * Uses JWT tokens stored in localStorage for authentication.
 */

import { ApiError } from '@/types/api-errors'
import type { AuthResponse, LoginCredentials, RegisterData, User } from '@/types/auth'
import { getAuthHeaders, removeToken, setToken } from './token'

const API_BASE = '/api/auth'

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
	const response = await fetch(`${API_BASE}/login`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(credentials),
	})

	if (!response.ok) {
		const errorData = await response.json().catch(() => null)
		throw ApiError.fromResponse(errorData, 'Fel e-post eller lösenord')
	}

	const data = (await response.json()) as AuthResponse & { token?: string }

	// Store the token if provided
	if (data.token) {
		setToken(data.token)
	}

	return data
}

/**
 * Register new user
 */
export async function register(data: RegisterData): Promise<AuthResponse> {
	const response = await fetch(`${API_BASE}/register`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(data),
	})

	if (!response.ok) {
		const errorData = await response.json().catch(() => null)
		throw ApiError.fromResponse(errorData, 'Kunde inte skapa konto')
	}

	const result = (await response.json()) as AuthResponse & { token?: string }

	// Store the token if provided
	if (result.token) {
		setToken(result.token)
	}

	return result
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
	try {
		await fetch(`${API_BASE}/logout`, {
			method: 'POST',
			headers: { ...getAuthHeaders() },
		})
	} finally {
		// Always remove token, even if logout request fails
		removeToken()
	}
}

/**
 * Refresh current session / get current user
 */
export async function refreshSession(): Promise<User | null> {
	try {
		const response = await fetch(`${API_BASE}/me`, {
			headers: { ...getAuthHeaders() },
		})

		if (!response.ok) {
			// Token is invalid or expired - remove it
			if (response.status === 401) {
				removeToken()
			}
			return null
		}

		// API returns user directly, not wrapped in { user: ... }
		const user = (await response.json()) as User
		return user
	} catch {
		return null
	}
}

/**
 * Update user profile
 */
export async function updateProfile(updates: Partial<User>): Promise<User> {
	const response = await fetch(`${API_BASE}/profile`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
			...getAuthHeaders(),
		},
		body: JSON.stringify(updates),
	})

	if (!response.ok) {
		const errorData = await response.json().catch(() => null)
		throw ApiError.fromResponse(errorData, 'Kunde inte uppdatera profil')
	}

	return response.json()
}
